import type {
    CommerceAddress,
    CommerceCart,
    CommerceCheckout,
    CommerceOrder,
    CommerceOrderStatus,
} from "@teqtank/commerce-contracts";
import { MockCommerceTransport } from "@teqtank/commerce-transport";
import { getCommerceOrderOperation } from "../operations/get-commerce-order-operation.js";
import { refreshCommerceOrderOperation } from "../operations/refresh-commerce-order-operation.js";

export function registerDefaultOrderOperations(
    transport: MockCommerceTransport,
    getCart: () => CommerceCart,
    getCheckout: () => CommerceCheckout | undefined,
): void {
    const attempts = new Map<string, number>();
    transport.register(getCommerceOrderOperation, ({ orderId }) => ({
        order: materialize(orderId, attempts.get(orderId) ?? 0, getCart(), getCheckout()),
    }));
    transport.register(refreshCommerceOrderOperation, ({ orderId }) => {
        const attempt = (attempts.get(orderId) ?? 0) + 1;
        attempts.set(orderId, attempt);
        return {
            order: materialize(orderId, attempt, getCart(), getCheckout()),
        };
    });
}

function materialize(
    orderId: string,
    attempt: number,
    cart: CommerceCart,
    checkout: CommerceCheckout | undefined,
): CommerceOrder {
    const status: CommerceOrderStatus =
        attempt >= 2 ? "completed" : attempt === 1 ? "processing" : "pending";
    const now = new Date();
    const address: CommerceAddress | null = checkout?.shippingAddress ?? null;
    return Object.freeze({
        id: orderId,
        orderNumber: orderId
            .replace(/^demo-/, "TT-")
            .replace(/[^a-z0-9-]/gi, "")
            .toUpperCase(),
        status,
        currencyCode: cart.currencyCode,
        email: checkout?.contact?.email ?? "customer@example.com",
        items: Object.freeze(
            cart.lines.map((line) =>
                Object.freeze({
                    id: line.id,
                    productId: line.productId,
                    variantId: line.variantId,
                    ...(line.displaySku === undefined ? {} : { displaySku: line.displaySku }),
                    name: line.name,
                    image: line.image,
                    quantity: line.quantity,
                    unitPrice: line.unitPrice,
                    lineTotal: line.lineTotal,
                }),
            ),
        ),
        totals: Object.freeze({
            subtotal: checkout?.totals.subtotal ?? cart.totals.subtotal,
            discount: checkout?.totals.discount ?? cart.totals.discount,
            shipping: checkout?.totals.shipping ?? cart.totals.shipping,
            tax: checkout?.totals.tax ?? cart.totals.tax,
            total: checkout?.totals.total ?? cart.totals.total,
        }),
        shippingAddress: address,
        billingAddress: checkout?.billingAddress ?? address,
        shipments: Object.freeze([
            Object.freeze({
                id: `shipment:${orderId}`,
                status: status === "completed" ? "preparing" : "pending",
                methodName:
                    checkout?.shippingMethods.find((item) => item.selected)?.name ??
                    "Standard delivery",
                estimatedDelivery:
                    checkout?.shippingMethods.find((item) => item.selected)?.estimatedDelivery ??
                    "3–5 business days",
                trackingNumber: null,
                trackingUrl: null,
            }),
        ]),
        receipt:
            status === "completed"
                ? Object.freeze({
                      id: `receipt:${orderId}`,
                      number: `R-${orderId.slice(-8).toUpperCase()}`,
                      issuedAt: now,
                      downloadUrl: null,
                  })
                : null,
        paymentSummary: "Payment captured securely",
        failureMessage: null,
        createdAt: now,
        updatedAt: now,
        completedAt: status === "completed" ? now : null,
    });
}
